import { useState, useEffect } from 'react';
import { getReferralLink, processReferralOnStart } from '../utils/referral';
import { getReferralCount } from '../lib/api';
import { haptic } from '../utils/haptic';
import { IconUsers, IconMail, IconShare, IconCopy } from './icons';
import './ReferralCard.css';

export type ReferralCardVariant = 'full' | 'strip';
const AMBASSADOR_TARGET = 10;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.top = '-1000px';
      textarea.style.left = '-1000px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

export function ReferralCard({ variant = 'full' }: { variant?: ReferralCardVariant }) {
  const [count, setCount] = useState(0);
  const [link, setLink] = useState('');
  const [linkReady, setLinkReady] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    void processReferralOnStart();

    getReferralCount()
      .then((c) => {
        if (!cancelled) setCount(c);
      })
      .catch(() => {
        if (!cancelled) setCount(0);
      });

    const resolveLink = (attempt: number) => {
      getReferralLink()
        .then((resolvedLink) => {
          if (cancelled) return;
          if (resolvedLink) {
            setLink(resolvedLink);
            setLinkReady(true);
            return;
          }
          if (attempt < 4) {
            retryTimer = setTimeout(() => resolveLink(attempt + 1), 1200);
            return;
          }
          setLink('');
          setLinkReady(true);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 4) {
            retryTimer = setTimeout(() => resolveLink(attempt + 1), 1200);
            return;
          }
          setLink('');
          setLinkReady(true);
        });
    };
    resolveLink(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  const canShare = Boolean(link);
  const linkDisplay = canShare
    ? link.replace(/^https?:\/\//, '')
    : linkReady
      ? 'Ссылка недоступна. Откройте Mini App из Telegram.'
      : 'Генерируем персональную ссылку...';

  const handleCopy = async () => {
    if (!canShare) {
      haptic.warning();
      return;
    }

    const ok = await copyText(link);
    if (ok) {
      setCopied(true);
      haptic.success();
      setTimeout(() => setCopied(false), 2000);
    } else {
      haptic.error();
    }
  };

  const handleShareChat = () => {
    if (!canShare) {
      haptic.warning();
      return;
    }

    haptic.action();
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent('Покажи что навайбкодил! 🚀')}`;
    const tg = (window as {
      Telegram?: { WebApp?: { openTelegramLink?: (url: string) => void; openLink?: (url: string) => void } };
    }).Telegram?.WebApp;

    if (tg?.openTelegramLink) {
      tg.openTelegramLink(shareUrl);
      return;
    }
    if (tg?.openLink) {
      tg.openLink(shareUrl);
      return;
    }
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const copyButtonAriaLabel = copied ? 'Скопировано' : 'Копировать';

  if (variant === 'strip') {
    return (
      <div className="referral-strip">
        <div className="referral-strip-icon">
          <IconUsers size={16} />
        </div>
        <div className="referral-strip-content">
          <div className="referral-strip-title">Пригласи друга</div>
          <div className="referral-strip-subtitle">{count}/{AMBASSADOR_TARGET} до «Амбассадор»</div>
        </div>
        <button
          type="button"
          className="referral-strip-btn"
          onClick={handleShareChat}
          disabled={!canShare}
          aria-disabled={!canShare}
        >
          <IconShare size={12} /> Позвать
        </button>
      </div>
    );
  }

  return (
    <div className="referral-card">
      <div className="referral-header">
        <span className="referral-icon" aria-hidden><IconUsers size={22} /></span>
        <div>
          <div className="referral-title">Пригласи друга</div>
          <div className="referral-subtitle">Получи бейдж «Амбассадор» за 10 друзей</div>
        </div>
      </div>

      <div className="referral-link-row">
        <code className={`referral-link${canShare ? '' : ' is-empty'}`}>{linkDisplay}</code>
        <button
          type="button"
          className="referral-copy-btn"
          onClick={handleCopy}
          aria-label={copyButtonAriaLabel}
          disabled={!canShare}
          aria-disabled={!canShare}
        >
          {copied ? '✓' : <IconCopy size={14} />}
        </button>
      </div>
      <button
        type="button"
        className="referral-share-btn"
        onClick={handleShareChat}
        disabled={!canShare}
        aria-disabled={!canShare}
      >
        <IconMail size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Отправить ссылку в чат
      </button>
      {!canShare && linkReady ? (
        <div className="referral-help">Ссылка появится после запуска приложения из Telegram.</div>
      ) : null}

      <div className="referral-stats">
        <div className="referral-stat">
          <div className="referral-stat-val">{count}</div>
          <div className="referral-stat-label">Приглашено</div>
        </div>
        <div className="referral-stat">
          <div className="referral-stat-val">{Math.max(0, AMBASSADOR_TARGET - count)}</div>
          <div className="referral-stat-label">До бейджа</div>
        </div>
      </div>
    </div>
  );
}
